import { AddFluxHOCProps, addFlux } from 'components/wrappers/WithFlux';
import { Filters, SearchFilters } from 'model/Filters';
import Experience from 'model/IExperience';
import { Aggregations } from 'model/SearchResults';
import { PureComponent, ReactNode } from 'react';

import { STATUS_PENDING } from '../../../constants';
import { SortType } from '../model/sorting';

interface OtherThingsTodo {
  title: string;
  data: Experience[];
}

export interface LandingPageHeaderContent {
  title?: string;
  titleKeys?: string[];
  image?: string;
  imageKeys?: string[];
  subtitle?: string;
  subtitleKeys?: string[];
  titleLandingPageHeader?: string;
  titleLandingPageHeaderKeys?: string[];
}

export interface SearchResultInterlinkingContent {
  keys: string[];
  label: string;
  name: string;
  value: string;
}

export interface SearchResultType {
  pages: {
    data: Experience[];
    status: number;
    page: number;
    aggregations?: Aggregations;
  }[];
  title?: string;
  titleKeys?: string[];
  description?: string;
  pagetitle?: string;
  seapage?: LandingPageHeaderContent;
  interlinks: SearchResultInterlinkingContent[];
  topSearchesTitleText?: string;
  topSearchesTitleKeys?: string[];
  maxResults: number;
  otherBySubcategory?: Experience[];
  otherThingsToDo?: OtherThingsTodo[];
  seoContentBlock?: {
    content?: string;
    title?: string;
  };
  titleAboveSearchResults: string;
  titleAboveSearchResultsKeys: string[];
  seeAllForCurrent: string;
  hostAggregations: any;
  categoryBlockTitle?: string;
  categoryBlockDescription?: string;
  totalNumberOfResults: number;
}

interface SearchResultsProviderState {
  searchResults: SearchResultType;
  filters: SearchFilters;
  sorting: SortType;
  isLoading: boolean;
}

const overrideFilters = (filters: Filters): Filters => {
  if (window.location.pathname.startsWith('/experiences')) {
    return {
      ...filters,
      activeFilters: filters.activeFilters.map((filter) => {
        if (filter.uid === 'adults') {
          return { ...filter, value: 1 };
        } else if (filter.uid === 'children') {
          return { ...filter, value: 0 };
        }
        return filter;
      }, []),
    };
  }
  return filters;
};

interface SearchResultsProviderProps {
  children(props: SearchResultsProviderState): ReactNode;
}

class SearchResultsProvider extends PureComponent<
  SearchResultsProviderProps & AddFluxHOCProps,
  SearchResultsProviderState
> {
  timeout1 = undefined;
  timeout2 = undefined;
  state = this.getStateFromStore();

  componentDidMount() {
    this.props.flux.stores.SearchStore.on('change', this.onStoreChange);
    this.props.flux.stores.FiltersStore.on('change', this.onStoreChange);
    if (
      !this.props.flux.stores.SearchStore.getSearchResult(this.state.filters)
    ) {
      this.timeout1 = setTimeout(() =>
        this.props.flux.actions.search.fetchSearchResults(
          overrideFilters(this.state.filters),
          this.state.sorting,
        ),
      );
    }
  }

  componentWillUnmount() {
    this.props.flux.stores.SearchStore.removeListener(
      'change',
      this.onStoreChange,
    );
    this.props.flux.stores.FiltersStore.removeListener(
      'change',
      this.onStoreChange,
    );
    clearTimeout(this.timeout1);
  }

  onStoreChange = () => {
    const oldSorting = this.state.sorting;
    this.setState(this.getStateFromStore(), () => {
      if (this.state.sorting !== oldSorting) {
        this.timeout2 = setTimeout(() =>
          this.props.flux.actions.search.fetchSearchResults(
            overrideFilters(this.state.filters),
            this.state.sorting,
          ),
        );
      }
    });
  };

  getStateFromStore() {
    const { SearchStore, FiltersStore } = this.props.flux.stores;
    const sorting = SearchStore.getSorting();
    const filters = FiltersStore.getFilters();
    const searchResults =
      SearchStore.getSearchResult(overrideFilters(filters)) || {};
    const lastPageIndex = (searchResults?.pages?.length ?? 1) - 1;
    const isLoading = [STATUS_PENDING, undefined].includes(
      searchResults?.pages?.[lastPageIndex]?.status ?? STATUS_PENDING,
    );

    return {
      filters,
      sorting,
      searchResults,
      isLoading,
    };
  }

  render() {
    return this.props.children(this.state);
  }
}

export default addFlux(SearchResultsProvider);
